/**
 * Formata valor para moeda BRL
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

/**
 * Gera dados de pagamento para envio, incluindo o título de cobrança gerado
 * (linhaDigitavel/pixCopiaECola/urlBoleto) pra ficar disponível até a tela final.
 */
export const buildPagamentoData = (tipoPagamento, valor, tituloData) => {
  return {
    tipo: tipoPagamento,
    valor,
    ...tituloData
  };
};

/**
 * Verifica se o pagamento está pronto para finalizar: precisa ter escolhido a
 * forma de pagamento e gerado o boleto/PIX de verdade.
 */
export const canFinalizarPagamento = (tipoPagamento, boletoGerado) => {
  return Boolean(tipoPagamento && boletoGerado);
};

/**
 * Arredonda para 2 casas decimais, evitando que arredondamentos de ponto flutuante
 * (ex: 0.1 + 0.2) apareçam no valor final cobrado do requerente.
 */
const arredondarMoeda = (valor) => Math.round((valor + Number.EPSILON) * 100) / 100;

/**
 * Calcula o valor da taxa de um pedido a partir do seu paymentConfig e do formData atual.
 * Suporta 3 modos, verificados nesta ordem:
 *  - quantityPricing: valor base + (quantidade "excedente" * valor unitário). Se `triggerField`
 *    for informado, só conta a quantidade quando esse campo tem o valor esperado (ex: taxa de
 *    averbação, que só existe se "possuiAverbacao" for SIM). Se `baseQuantity` for informado, esse
 *    número de unidades já está incluso no valor base e não gera cobrança extra (ex: taxa de
 *    sociedade pluripessoal, que já inclui os 2 primeiros sócios). A quantidade é sempre
 *    arredondada para inteiro (não existe "meio sócio" nem "meia averbação").
 *  - dynamicAmount + options: valor fixo escolhido de uma tabela, por uma opção de radio/select.
 *    A opção pode ter seu próprio `quantityPricing` (mesmo formato acima), e aí o `amount` dela
 *    vira o valor base (ex: Distrato de Sociedade Pluripessoal, cobrado por sócio além do 2º).
 *  - amount: valor fixo simples (ou 0 = isento).
 */
export const calculatePaymentAmount = (paymentConfig, formData = {}) => {
  if (!paymentConfig) return 0;

  if (paymentConfig.quantityPricing) {
    const { triggerField, triggerValue, quantityField, unitAmount, baseQuantity = 0 } = paymentConfig.quantityPricing;
    const baseAmount = paymentConfig.amount || 0;
    const isActive = !triggerField || formData[triggerField] === triggerValue;
    const quantidadeInformada = isActive ? Math.round(Number(formData[quantityField]) || 0) : 0;
    const quantidadeCobravel = Math.max(0, quantidadeInformada - baseQuantity);
    return arredondarMoeda(baseAmount + quantidadeCobravel * unitAmount);
  }

  if (paymentConfig.dynamicAmount && paymentConfig.options) {
    const dynamicField = paymentConfig.dynamicField || 'tipoServico';
    const selectedValue = formData[dynamicField];

    const opcao = selectedValue && paymentConfig.options[selectedValue];
    if (opcao) {
      if (opcao.quantityPricing) {
        return calculatePaymentAmount({ amount: opcao.amount, quantityPricing: opcao.quantityPricing }, formData);
      }
      return arredondarMoeda(opcao.amount);
    }
  }

  return arredondarMoeda(paymentConfig.amount || 0);
};

/**
 * Descreve qual opção/quantidade determinou o valor calculado por calculatePaymentAmount,
 * para exibir em telas de resumo/protocolo. Cobre os mesmos modos de paymentConfig.
 */
export const describePaymentSelection = (paymentConfig, formData = {}) => {
  const vazio = { opcaoSelecionada: null, opcaoNome: null, opcaoDescricao: null };
  if (!paymentConfig) return vazio;

  if (paymentConfig.quantityPricing) {
    const { quantityField } = paymentConfig.quantityPricing;
    const quantidade = formData[quantityField];
    if (!quantidade) return vazio;
    return {
      opcaoSelecionada: quantidade,
      opcaoNome: `Quantidade: ${quantidade}`,
      opcaoDescricao: null
    };
  }

  if (paymentConfig.dynamicAmount && paymentConfig.options) {
    const dynamicField = paymentConfig.dynamicField || 'tipoServico';
    const opcaoEscolhida = formData[dynamicField];
    const opcaoDetalhes = paymentConfig.options?.[opcaoEscolhida];
    const quantidade = opcaoDetalhes?.quantityPricing && formData[opcaoDetalhes.quantityPricing.quantityField];
    const opcaoNome = opcaoDetalhes?.name
      ? (quantidade ? `${opcaoDetalhes.name} - Quantidade: ${quantidade}` : opcaoDetalhes.name)
      : null;
    return {
      opcaoSelecionada: opcaoEscolhida || null,
      opcaoNome,
      opcaoDescricao: opcaoDetalhes?.description || null
    };
  }

  return vazio;
};

/**
 * Preço de referência de um pedido para exibição em listagens (antes de o requerente
 * preencher o formulário). `isVariable` indica se esse é só o valor mínimo/inicial e a
 * tela deveria prefixar com "a partir de" (taxa por quantidade ou por opção variável).
 */
export const getStartingPaymentInfo = (paymentConfig) => {
  if (!paymentConfig) return { amount: 0, isVariable: false };

  if (paymentConfig.quantityPricing) {
    return { amount: paymentConfig.amount || 0, isVariable: true };
  }

  if (paymentConfig.dynamicAmount && paymentConfig.options) {
    const valores = Object.values(paymentConfig.options).map(opcao => opcao.amount);
    const menorValor = valores.length ? Math.min(...valores) : (paymentConfig.amount || 0);
    return { amount: menorValor, isVariable: true };
  }

  return { amount: paymentConfig.amount || 0, isVariable: false };
};